import { Calendar, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router-dom'
import { BlogLayout } from '@/components/blog/BlogLayout'
import { BlogTagChip } from '@/components/blog/BlogTagChip'
import { SEO } from '@/components/seo/SEO'
import { blogLocaleFor, formatBlogDate, getPostBySlug } from '@/lib/blog'

export default function BlogPost() {
  const { t, i18n } = useTranslation()
  const { slug } = useParams<{ slug: string }>()
  const post = slug ? getPostBySlug(slug, i18n.language) : undefined

  if (!post) {
    return <Navigate to="/blog" replace />
  }

  return (
    <>
      <SEO
        title={`${post.title} | Equna Blog`}
        description={post.description}
        canonical={`/blog/${post.slug}`}
        type="article"
        ogImage={post.cover || undefined}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: post.title,
          description: post.description,
          datePublished: post.date,
          dateModified: post.date,
          author: {
            '@type': 'Person',
            name: post.author,
          },
          publisher: {
            '@type': 'Organization',
            name: 'Equna',
            url: 'https://equna.eu',
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://equna.eu/blog/${post.slug}`,
          },
          inLanguage: blogLocaleFor(post.language),
          ...(post.cover ? { image: post.cover } : {}),
        }}
      />
      <BlogLayout showBackToBlog>
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {post.cover && (
            <div className="mb-8 rounded-2xl overflow-hidden bg-slate-100 aspect-[16/9]">
              <img
                src={post.cover}
                alt=""
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          )}

          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mb-4">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                <time dateTime={post.date}>
                  {formatBlogDate(post.date, blogLocaleFor(post.language))}
                </time>
              </span>
              <span aria-hidden="true">·</span>
              <span className="inline-flex items-center gap-1.5">
                <User className="w-4 h-4" />
                {post.author}
              </span>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 leading-tight mb-4">
              {post.title}
            </h1>

            {post.description && (
              <p className="text-lg text-slate-600 leading-relaxed">{post.description}</p>
            )}

            {post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-6">
                {post.tags.map((tag) => (
                  <BlogTagChip key={tag} tag={tag} />
                ))}
              </div>
            )}
          </header>

          <div
            className="prose prose-slate max-w-none prose-headings:font-bold prose-headings:text-slate-900 prose-a:text-[#237CCA] hover:prose-a:text-[#1a5f9f] prose-strong:text-slate-900 prose-img:rounded-xl"
            dangerouslySetInnerHTML={{ __html: post.contentHtml }}
          />

          <hr className="my-12 border-slate-200" />

          <div className="text-sm text-slate-500">
            <p>
              {t('blog.footerCta.text')}{' '}
              <a
                href="mailto:contact@equna.eu"
                className="text-[#237CCA] hover:text-[#1a5f9f] font-semibold"
              >
                contact@equna.eu
              </a>
              .
            </p>
          </div>
        </article>
      </BlogLayout>
    </>
  )
}
